import CloseIcon from '@mui/icons-material/Close'
import RotateIcon from '@mui/icons-material/RotateRight'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import TallImageWarning from 'components/TallImageWarning'
import { LEGACY_URL } from 'config'
import { FileType } from 'interfaces'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { isIOS, isMobileBrowser } from 'utils/browser'
import {
  ALLOWED_BULK_EXTENSIONS,
  ALLOWED_PHOTO_FORMATS,
  ALLOWED_STATIC_PHOTO_FORMATS,
  ALLOWED_VIDEO_FORMATS,
  FILE_FORMAT_GIF,
  INPUT_ACCEPT_ALL,
  INPUT_ACCEPT_ALL_MEDIA,
  INPUT_ACCEPT_BULK,
  INPUT_ACCEPT_GIF,
  INPUT_ACCEPT_PHOTO,
  INPUT_ACCEPT_PHOTO_STATIC,
  INPUT_ACCEPT_VIDEO,
  MAX_GIF_SIZE_BYTES,
  MAX_PHOTO_SIZE_BYTES,
  MAX_VIDEO_SIZE_BYTES, MAX_VIDEO_SIZE_MB
} from 'utils/file/constants'

import { getFileTypeByMimeType } from 'utils/file'
import { bytesToSize } from 'shared'
import styles from './FileUploader.pcss'

const variables = require('styles/variables')

const PROGRESS_TRANSITION_TIME = variables['--transition-duration-simple']
const LOADED_MAX_VALUE = 100
const PROGRESSBAR_SIZE = 60
const BASE_TEN = 10

export enum FileUploaderView {
  Photo,
  Video,
  Bulk,
  Gif,
  All,
  Inactive
}

const labels: { [key: number]: string } = {
  [FileUploaderView.Photo]: 'image',
  [FileUploaderView.Video]: 'video',
  [FileUploaderView.Bulk]: 'spreadsheet file',
  [FileUploaderView.All]: 'file',
  [FileUploaderView.Inactive]: 'file',
  [FileUploaderView.Gif]: 'gif'
}

export interface FileUploaderProps {
  view: FileUploaderView
  file?: File
  className?: string
  hideCloseButton?: boolean
  noGifAsPhoto?: boolean
  photoSizeLimit?: number
  gifSizeLimit?: number
  videoSizeLimit?: number
  rotatePhotoBy?: number
  imagePreviewClassName?: string
  videoPreviewClassName?: string
  clearExcelFileButton?: React.ReactNode
  onPhotoRotateClick?: () => void
  onClose?: () => void
  onFileUploaded: (file: File) => void
  onError: (error: string) => void
}

enum FileUploadState {
  Normal,
  Uploading,
  Completed
}

interface FileUploaderState {
  activeState: FileUploadState
  loaded: number
  imageSource?: string
  videoSource?: string
  bulkUploadFileName?: string
  fileType?: FileType
}

export function FileUploader(props: FileUploaderProps) {
  const intl = useIntl()
  const [state, setState] = useState<FileUploaderState>({
    activeState: FileUploadState.Normal,
    loaded: 0
  })

  const fileReaderRef = useRef<FileReader | null>(null)
  const imageElementRef = useRef<HTMLImageElement>(null)
  const videoElementRef = useRef<HTMLVideoElement>(null)
  const canvasElementRef = useRef<HTMLCanvasElement>(null)
  const updateProgressBarIdRef = useRef<number | null>(null)
  const loadedRef = useRef(0)
  const contentElementRef = useRef<HTMLDivElement>(null)
  const sectionRef = useRef<HTMLElement>(null)

  const ERROR_FORMAT_NOT_SUPPORTED = useMemo(
    () => intl.formatMessage({ id: 'file.messages.invalid-type' }),
    [intl]
  )

  const allowedPhotoFormats = useMemo(() => {
    if (props.view === FileUploaderView.Gif) {
      return [FILE_FORMAT_GIF]
    }
    return props.noGifAsPhoto ? ALLOWED_STATIC_PHOTO_FORMATS : ALLOWED_PHOTO_FORMATS
  }, [props.view, props.noGifAsPhoto])

  const photoSizeLimit = props.photoSizeLimit || MAX_PHOTO_SIZE_BYTES
  const videoSizeLimit = props.videoSizeLimit || MAX_VIDEO_SIZE_BYTES
  const gifSizeLimit = props.gifSizeLimit || MAX_GIF_SIZE_BYTES

  const getContentElement = useCallback(() => {
    return contentElementRef.current
  }, [])

  const drawPlayButton = useCallback((context: CanvasRenderingContext2D) => {
    if (!canvasElementRef.current) {
      return
    }

    const centerX = canvasElementRef.current.width / 2
    const centerY = canvasElementRef.current.height / 2
    const radius = 30
    const triangleSize = 24
    const centerXOffset = 2
    const btnX = centerX + centerXOffset
    const transparentWhite = 'rgba(255, 255, 255, 0.7)'
    const darkGray = '#333333'

    context.beginPath()
    context.arc(centerX, centerY, radius, 0, 2 * Math.PI, false)
    context.fillStyle = transparentWhite
    context.fill()

    context.fillStyle = darkGray
    context.beginPath()
    context.moveTo(btnX - triangleSize / 2, centerY - triangleSize / 2)
    context.lineTo(btnX + triangleSize / 2, centerY)
    context.lineTo(btnX - triangleSize / 2, centerY + triangleSize / 2)
    context.fill()
  }, [])

  const renderVideoPreview = useCallback(() => {
    if (videoElementRef.current) {
      videoElementRef.current.addEventListener('loadeddata', () => {
        const context = canvasElementRef.current?.getContext('2d')
        if (!context) {
          return
        }

        const canvasWidth = canvasElementRef.current!.width
        context.drawImage(videoElementRef.current!, 0, 0, canvasWidth, videoElementRef.current!.clientHeight)
        drawPlayButton(context)
      })
    }

    if (canvasElementRef.current) {
      canvasElementRef.current.addEventListener('click', () => {
        if (videoElementRef.current && canvasElementRef.current) {
          videoElementRef.current.play()
          canvasElementRef.current.style.display = 'none'
        }
      })
    }
  }, [drawPlayButton])

  const validateBulk = useCallback((file: File) => {
    const extension = file.name.substring(file.name.lastIndexOf('.'))
    const isValid = ALLOWED_BULK_EXTENSIONS.indexOf(extension) !== -1
    if (!isValid) {
      props.onError(ERROR_FORMAT_NOT_SUPPORTED)
    }
    return isValid
  }, [props, ERROR_FORMAT_NOT_SUPPORTED])

  const validateVideo = useCallback((file: File): boolean => {
    const maxSizeFormatted = bytesToSize(videoSizeLimit)
    const maxSizePrettyString = `${parseInt(maxSizeFormatted, BASE_TEN)} ${maxSizeFormatted.split(' ')[1]}`
    let error

    if (file.size > videoSizeLimit) {
      error = intl.formatMessage({ id: 'file.messages.video-size-exceeded' }, { size: maxSizePrettyString })
    }

    if (ALLOWED_VIDEO_FORMATS.indexOf(file.type) === -1) {
      error = ERROR_FORMAT_NOT_SUPPORTED
    }

    if (error) {
      props.onError(error)
      return false
    }
    return true
  }, [videoSizeLimit, intl, ERROR_FORMAT_NOT_SUPPORTED, props])

  const validatePhoto = useCallback((file: File): boolean => {
    const maxSize = file.type === FILE_FORMAT_GIF ? gifSizeLimit : photoSizeLimit
    const maxSizeFormatted = bytesToSize(maxSize)
    const maxSizePrettyString = `${parseInt(maxSizeFormatted, BASE_TEN)} ${maxSizeFormatted.split(' ')[1]} `

    if (file.size > maxSize) {
      const error = intl.formatMessage({ id: 'file.messages.photo-size-exceeded' }, { size: maxSizePrettyString })
      props.onError(error)
      return false
    }

    if (allowedPhotoFormats.indexOf(file.type) === -1) {
      props.onError(ERROR_FORMAT_NOT_SUPPORTED)
      return false
    }
    return true
  }, [photoSizeLimit, gifSizeLimit, intl, allowedPhotoFormats, ERROR_FORMAT_NOT_SUPPORTED, props])

  const handleError = useCallback((message: string) => {
    setState(prev => ({ ...prev, activeState: FileUploadState.Normal }))
    props.onError(message)
  }, [props])

  const updateProgressBar = useCallback(() => {
    if (loadedRef.current <= LOADED_MAX_VALUE) {
      setState(prev => ({ ...prev, loaded: loadedRef.current }))
      return
    }

    setState(prev => ({ ...prev, loaded: 100 }))
    setTimeout(() => {
      setState(prev => ({ ...prev, activeState: FileUploadState.Completed }))
    }, PROGRESS_TRANSITION_TIME)
  }, [])

  const onFileLoaded = useCallback((fileReader: FileReader, file: File, fileType: FileType) => {
    let nextState: Partial<FileUploaderState>

    switch (fileType) {
      case FileType.Image:
      case FileType.Gif:
        nextState = { imageSource: fileReader.result as string }
        break
      case FileType.Video:
        nextState = { videoSource: URL.createObjectURL ? URL.createObjectURL(file) : fileReader.result as string }
        break
      case FileType.Spreadsheet:
        nextState = { bulkUploadFileName: file.name }
        break
      default:
        throw new Error('Invalid file type')
    }

    setState(prev => ({ ...prev, ...nextState }))
    props.onFileUploaded(file)
  }, [props])

  const loadFile = useCallback((file: File) => {
    const fileType = props.view === FileUploaderView.Bulk
      ? FileType.Spreadsheet
      : getFileTypeByMimeType(file.type)

    if (
      (fileType === FileType.Image && !validatePhoto(file))
      || (fileType === FileType.Gif && !validatePhoto(file))
      || (fileType === FileType.Video && !validateVideo(file))
      || (fileType === FileType.Spreadsheet && !validateBulk(file))
    ) {
      return
    }

    fileReaderRef.current = new FileReader()
    setState(prev => ({ ...prev, activeState: FileUploadState.Uploading, fileType }))

    fileReaderRef.current.onprogress = (progress) => {
      const percentageMultiplier = 100
      const loaded = (progress.loaded / progress.total) * percentageMultiplier
      if (loadedRef.current !== Math.ceil(loaded)) {
        loadedRef.current = Math.ceil(loaded)
        updateProgressBarIdRef.current = requestAnimationFrame(updateProgressBar)
      }
    }

    fileReaderRef.current.onload = (event) => {
      loadedRef.current = LOADED_MAX_VALUE + 1
      updateProgressBarIdRef.current = requestAnimationFrame(updateProgressBar)
      onFileLoaded(event.target as FileReader, file, fileType as FileType)
    }

    fileReaderRef.current.onerror = () => {
      handleError(intl.formatMessage({ id: 'composer.errors.upload-generic' }))
    }

    fileReaderRef.current.readAsDataURL(file)
  }, [props.view, validatePhoto, validateVideo, validateBulk, updateProgressBar, onFileLoaded, handleError, intl])

  const onFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (file) {
      loadFile(file)
    }
  }, [loadFile])

  const cancelWindowDragEvents = useCallback((e: Event) => {
    e.preventDefault()
  }, [])

  const onFileDropped = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault()
    const contentEl = getContentElement()
    if (contentEl) {
      contentEl.classList.remove(styles['drag-hover'])
    }
    if (e.dataTransfer.files?.[0]) {
      loadFile(e.dataTransfer.files[0])
    }
  }, [loadFile, getContentElement])

  const onDragEnter = useCallback(() => {
    const contentEl = getContentElement()
    if (contentEl && !contentEl.classList.contains(styles['drag-hover'])) {
      contentEl.classList.add(styles['drag-hover'])
    }
  }, [getContentElement])

  const onDragLeave = useCallback(() => {
    const contentEl = getContentElement()
    if (contentEl) {
      contentEl.classList.remove(styles['drag-hover'])
    }
  }, [getContentElement])

  const getUploaderAcceptedTypes = useCallback(() => {
    let accept: string
    
    if (props.view === FileUploaderView.Photo) {
      const formats = props.noGifAsPhoto ? INPUT_ACCEPT_PHOTO_STATIC : INPUT_ACCEPT_PHOTO
      accept = Array.isArray(formats) ? formats.join(',') : formats
    } else if (props.view === FileUploaderView.Gif) {
      accept = INPUT_ACCEPT_GIF
    } else if (props.view === FileUploaderView.Video) {
      const formats = INPUT_ACCEPT_VIDEO
      accept = Array.isArray(formats) ? formats.join(',') : formats
    } else if (props.view === FileUploaderView.Bulk) {
      accept = INPUT_ACCEPT_BULK
    } else if (props.view === FileUploaderView.All) {
      accept = INPUT_ACCEPT_ALL_MEDIA
    } else {
      accept = INPUT_ACCEPT_ALL
    }
    
    return accept
  }, [props.view, props.noGifAsPhoto])

  const renderSampleExcelFileLink = useCallback(() => {
    const bulkUploadSampleUrl = `${LEGACY_URL}/resources/sample_bulk_posts_import.xlsx`
    return (
      <a
        key="bulk-sample"
        className={styles['bulk-download-link']}
        href={bulkUploadSampleUrl}
      >
        <FormattedMessage id="composer.actions.download-excel" />
      </a>
    )
  }, [])

  const getUploaderHint = useCallback(() => {
    switch (props.view) {
      case FileUploaderView.Video:
        return (
          <p key="video-info" className={styles.info}>
            <FormattedMessage
              id="composer.labels.video-note"
              defaultMessage="(We only support video files under {size} MB at this time.)"
              values={{ size: MAX_VIDEO_SIZE_MB }}
            />
          </p>
        )

      case FileUploaderView.Bulk:
        return (
          <p key="bulk-info">
            {renderSampleExcelFileLink()}
          </p>
        )

      default:
        return null
    }
  }, [props.view, renderSampleExcelFileLink])

  const renderUploadCompletedView = useCallback(() => {
    switch (state.fileType) {
      case FileType.Image:
      case FileType.Gif: {
        const style = props.rotatePhotoBy !== undefined && state.fileType !== FileType.Gif
          ? { transform: `rotate(${props.rotatePhotoBy}deg)` }
          : undefined
        return (
          <img
            key="image"
            style={style}
            src={state.imageSource}
            className={`${styles.image} ${props.imagePreviewClassName || ''}`}
            ref={imageElementRef}
            alt="preview"
          />
        )
      }

      case FileType.Video: {
        const textNoPreview = 'No preview available.'
        return (
          <div key="video-wrapper" className={styles['video-wrapper']}>
            <video
              key="video"
              src={state.videoSource}
              controls
              className={`${styles.video} ${props.videoPreviewClassName || ''}`}
            >
              {textNoPreview}
            </video>
            {isIOS() && <canvas key="canvas" className={styles.canvas} ref={canvasElementRef} />}
          </div>
        )
      }

      case FileType.Spreadsheet: {
        const fileUploadedText = 'Uploaded excel file'
        return (
          <div key="bulk-upload-complete">
            <p key="bulk-upload-p1" className={styles['bulk-complete']}>
              {fileUploadedText}
            </p>
            <p key="bulk-file-name" className={`${styles['bulk-file-name']} text-ellipsis`}>
              {state.bulkUploadFileName}
              {props.clearExcelFileButton}
            </p>
            {renderSampleExcelFileLink()}
          </div>
        )
      }

      default:
        return null
    }
  }, [state.fileType, state.imageSource, state.videoSource, state.bulkUploadFileName, props, renderSampleExcelFileLink])

  const renderState = useCallback((uploadState: FileUploadState) => {
    switch (uploadState) {
      case FileUploadState.Normal: {
        const accept = getUploaderAcceptedTypes()
        return (
          <>
            <UploaderTransitionHint view={props.view} key="hint" />
            <p key="message-line-2">or</p>
            <p key="upload-btn-wrapper" className={styles['btn-upload']}>
              <FormattedMessage
                id={`composer.actions.choose-${props.view === FileUploaderView.Bulk ? 'excel' : 'file'}`}
              />
              <input
                key="input"
                type="file"
                accept={accept}
                onChange={onFileSelected}
              />
            </p>
            {getUploaderHint()}
          </>
        )
      }

      case FileUploadState.Uploading:
        return (
          <>
            <div key="loading-anim" className={styles['loading-container']}>
              <CircularProgress
                key="progress"
                color="primary"
                value={state.loaded}
                variant="determinate"
                size={PROGRESSBAR_SIZE}
              />
              <span key="percent">{state.loaded}%</span>
            </div>
            <p key="loading-msg">
              <FormattedMessage
                id="composer.labels.uploading"
                defaultMessage={`Uploading your ${labels[props.view]}...`}
                values={{ file: labels[props.view] }}
              />
            </p>
          </>
        )

      case FileUploadState.Completed:
        return renderUploadCompletedView()

      default:
        throw new Error('Invalid FileUploader State')
    }
  }, [getUploaderAcceptedTypes, onFileSelected, getUploaderHint, state.loaded, props.view, renderUploadCompletedView])

  const onClose = useCallback(() => {
    if (props.onClose) {
      props.onClose()
    }
  }, [props])

  // Mount, unmount, and file prop effects
  useEffect(() => {
    window.addEventListener('dragover', cancelWindowDragEvents, false)
    window.addEventListener('drop', cancelWindowDragEvents, false)

    // Load file if passed in props on mount
    if (props.file) {
      loadFile(props.file)
    }

    return () => {
      if (fileReaderRef.current) {
        fileReaderRef.current.abort()
      }

      if (updateProgressBarIdRef.current) {
        cancelAnimationFrame(updateProgressBarIdRef.current)
      }

      window.removeEventListener('dragover', cancelWindowDragEvents)
      window.removeEventListener('drop', cancelWindowDragEvents)
    }
  }, [cancelWindowDragEvents, props.file, loadFile])

  // Reset upload state when view changes
  useEffect(() => {
    setState(prev => ({ ...prev, activeState: FileUploadState.Normal }))
    if (fileReaderRef.current) {
      fileReaderRef.current.abort()
    }
  }, [props.view])

  // Handle file prop change (in case file prop is updated after mount)
  useEffect(() => {
    // Only load file if it changed and state is back to Normal
    // This allows the file prop to be updated without aborting current upload
    if (props.file && state.activeState === FileUploadState.Normal) {
      loadFile(props.file)
    }
  }, [props.file, loadFile, state.activeState])

  // Handle iOS video preview
  useEffect(() => {
    if (
      isIOS()
      && state.videoSource
      && state.activeState === FileUploadState.Completed
    ) {
      if (sectionRef.current) {
        const video = sectionRef.current.querySelector('video') as HTMLVideoElement
        const canvas = sectionRef.current.querySelector('canvas') as HTMLCanvasElement
        videoElementRef.current = video
        canvasElementRef.current = canvas
        renderVideoPreview()
      }
    }
  }, [state.videoSource, state.activeState, renderVideoPreview])

  const imageUploadedState = state.imageSource
    && state.fileType === FileType.Image
    && state.activeState === FileUploadState.Completed

  const tallImageWarning = imageUploadedState
    && imageElementRef.current
    && imageElementRef.current.height > parseInt(variables['--card-image-max-height'], BASE_TEN)
    ? <TallImageWarning key="tall-image" withWrapper={true} />
    : null

  const classNameImage = imageUploadedState ? `${styles['preview-image']}` : ''
  const mobileClass = state.activeState === FileUploadState.Normal && isMobileBrowser() ? styles['mobile-hide'] : ''
  const className = `${styles.wrapper} ${props.className || ''} ${mobileClass}`

  return (
    <section
      key="uploader"
      className={className}
      ref={sectionRef}
      onDrop={onFileDropped}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
    >
      {props.onClose && (
        <IconButton
          key="btn-close"
          className={`${styles['btn-close']} ${props.hideCloseButton ? styles['btn-close-hidden'] : ''}`}
          size="small"
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      )}
      {imageUploadedState && props.rotatePhotoBy !== undefined && (
        <IconButton
          key="btn-rotate"
          className={styles['btn-rotate']}
          size="small"
          onClick={props.onPhotoRotateClick}
        >
          <RotateIcon />
        </IconButton>
      )}
      <div
        key="content"
        className={`${styles.content} ${classNameImage}`}
        ref={contentElementRef}
      >
        {renderState(state.activeState)}
        {tallImageWarning}
      </div>
    </section>
  )
}

export default FileUploader

const HINT_TRANSITION_TIMEOUT = 2000 // ms
const HINT_LOOP_MAP: any = {
  [FileUploaderView.Photo]: {
    label: labels[FileUploaderView.Photo],
    next: FileUploaderView.Video
  },
  [FileUploaderView.Video]: {
    label: labels[FileUploaderView.Video],
    next: FileUploaderView.Gif
  },
  [FileUploaderView.Gif]: {
    label: labels[FileUploaderView.Gif],
    next: FileUploaderView.Photo
  }
}

export const UploaderTransitionHint = (props: { view: FileUploaderView }) => {
  const [current, setCurrent] = useState(HINT_LOOP_MAP[props.view] || HINT_LOOP_MAP[FileUploaderView.Photo])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null
    if (props.view === FileUploaderView.All) {
      timeout = setTimeout(() => {
        setCurrent(HINT_LOOP_MAP[current.next])
      }, HINT_TRANSITION_TIMEOUT)
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [props.view, current])

  return (
    <p key="message-line-1">
      Drop
      <span
        key={current.label}
        className={`${styles['hint-transition']} ${props.view === FileUploaderView.All ? styles['with-anim'] : ''}`}
      >
        {props.view !== FileUploaderView.All ? labels[props.view] : current.label}
      </span>
      here
    </p>
  )
}
